/** Point and comment (#717). The picker's code, and the capture libraries with it, loads the first time it is turned on. */
import { lazy, Suspense, useEffect, type ComponentType } from "react";
import { useToast } from "../../kit";

type PickerProps = { onClose: () => void };

/** The picker's code did not arrive (a deploy replaced it, the network dropped): say so and turn the picker off. Never
 * the screen's error page. */
function Unavailable({ onClose }: PickerProps) {
  const toast = useToast();
  useEffect(() => {
    toast.show({ message: "Point and comment did not load. Reload the page and try again." });
    onClose();
  }, [toast, onClose]);
  return null;
}

const Picker = lazy((): Promise<{ default: ComponentType<PickerProps> }> => import("./Picker").catch(() => ({ default: Unavailable })));

/** While the code loads, a press lands on nothing (never on the page under it), and Esc turns the picker off. */
function Loading({ onClose }: PickerProps) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopImmediatePropagation();
      onClose();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose]);
  return <div aria-hidden className="pointer-events-auto fixed inset-0 z-[70] cursor-progress" />;
}

export function PickHost({ open, onClose }: { open: boolean; onClose: () => void }) {
  if (!open) return null;
  return (
    <Suspense fallback={<Loading onClose={onClose} />}>
      <Picker onClose={onClose} />
    </Suspense>
  );
}
